/** images */
import React, { useState, useEffect, useContext, useRef } from 'react';

import cookie from 'react-cookies';
import { NotificationManager } from 'react-notifications';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import moment from 'moment';

import {
  shouldDisplayCreditUnfreeze,
  RenderSubtitle,
  RenderButtonsFn,
  RenderSectionTitle,
  RenderCurrentSection,
  SetActiveFeature,
  handleSaveFileProperty,
  handleSkipToNextSection,
  handleGoBackSection,
} from './index';

import TenantConfig from '../../config';
import { isApplicationWIP } from '../../constants/status';
import { UserContext } from '../../context/UserContext';
import { isPrimaryApplicant, isPropertyInSection } from '../../helpers/applicationHelp';

/** helpers */
import { formatUSDCurrency } from '../../helpers/format';
import { validateInputField } from '../../helpers/validation';
import { useRudder } from '../../hooks/rudder';
import { useForm } from '../../hooks/useForm';

/** service */
import { updateApplication, validateProperty } from '../../service/api';
import OfferDetail from '../common/OfferDetail';
import Stepper from '../common/Stepper';

function PageForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getPropertyFields } = useForm();

  let { appData, updateAppData, setLoading, profile } = useContext(UserContext);
  const { identify, leavePage, errorEvent, clickEvent } = useRudder();

  if (!appData || !appData.applicationFeatures || !appData?.id) {
    navigate('/dasboard');
  }
  // todo we need to filter properties based on phases too somehow
  let properties;
  let features;
  //we filter to only show feature matching phase
  if (isApplicationWIP(appData.status)) {
    features = appData.applicationFeatures;
  } else {
    features = appData.applicationFeatures
      ? appData.applicationFeatures.filter((feature) => {
          // we ignore file props if there are no files actually requested / declined
          return (
            feature.phase === appData.phase &&
            appData.sectionSequence.includes(feature.section) &&
            (feature.itemType !== 'FileProperty' ||
              (feature.itemType === 'FileProperty' &&
                feature.value.filter(
                  (file) =>
                    (file.status === 'REQUESTED' || file.status === 'DECLINED') &&
                    !file.fileType.includes('bankVerificationStatement')
                ).length > 0))
          );
        })
      : [];
  }
  let filteredSections = features.map((feature) => {
    if (feature.section) return feature.section;
  });
  filteredSections = [...new Set(filteredSections)];

  //we add properties that were not there originally

  if (isApplicationWIP(appData.status)) {
    properties = appData.propertySequence;
  } else {
    properties = features.map((feature) => {
      if (feature.name) return feature.name;
    });
  }

  let isEdit = new URLSearchParams(location.search).get('edit');
  const propertyIndex = new URLSearchParams(location.search).get('propertyIndex');
  const sectionIndex = new URLSearchParams(location.search).get('sectionIndex');
  const editOwnerIndex = new URLSearchParams(location.search).get('ownerIndex');

  const [data, setData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState({});
  const [dataError, setDataError] = useState({});
  const [error, setError] = useState('');
  const [sectionError, setSectionError] = useState('');
  const [plaidState, setPlaidState] = useState('');
  const [activeIndex, setActiveIndex] = useState({
    sectionIndex: sectionIndex ? parseInt(sectionIndex) : 0,
    propertyIndex: propertyIndex ? parseInt(propertyIndex) : 0,
  });
  const [isShowCreateForm, setIsShowCreateForm] = useState(editOwnerIndex ? 0 : 1);
  const [owners, setOwners] = useState([]);
  const [completed, setCompleted] = useState(0);
  const [showCreditReportModal, setShowCreditReportModal] = useState(false);
  const [isMainApplicant, setIsMainApplicant] = useState(true);
  const [closeTimes, setCloseTimes] = useState(0); // How many times did you close the Plaid ?

  const [moveSectionOnCreditScoreModalClose, setMoveSectionOnCreditScoreModalClose] =
    useState(false);

  const finalFilteredSections = filteredSections.filter((value) =>
    appData.sectionSequence.includes(value)
  );

  const sections = TenantConfig?.stepper.showApplyStep
    ? [...finalFilteredSections, appData.phase === 'PRE_SUBMISSION' ? 'Apply' : 'Review']
    : finalFilteredSections;

  const appDataRef = useRef();
  appDataRef.current = appData;
  const stateRef = useRef();
  stateRef.current = data;
  const maskInputEl = useRef();
  /** initial activeIndex set up */
  useEffect(() => {
    identify();
    window.addEventListener('beforeunload', () => leavePage());

    window.addEventListener('click', clickEvent);
    const lIsMainApplicant = isPrimaryApplicant(appDataRef.current);
    setIsMainApplicant(lIsMainApplicant);
    //TODO: Not sure if we need it or not
    // removeCreateForm();
    let hasInProgressFeature = false;
    const oauth_state_id = cookie.load('oauth_state_id');

    if (!isEdit) {
      sections.map((section, sectionIndex) => {
        if (section === 'Apply' || section === 'Review') {
          return;
        }
        properties
          .filter((property) => isPropertyInSection(features, section, property))
          .map((property, propertyIndex) => {
            const feature = features.find((feature) => feature.name === property);
            if (oauth_state_id && feature.dataType == 'plaidtoken') {
              setActiveIndex({
                sectionIndex,
                propertyIndex,
              });
            }
            if (lIsMainApplicant && feature.valueValidated === false && !hasInProgressFeature) {
              hasInProgressFeature = true;
              if (!oauth_state_id) {
                setActiveIndex({
                  sectionIndex,
                  propertyIndex,
                });
              }
              setCompleted(sectionIndex);
            }
          });
      });
      if (lIsMainApplicant && !hasInProgressFeature) {
        if (!oauth_state_id) {
          navigate('/review');
        } else {
          isEdit = true;
          setCompleted(sections.length - 1);
        }
      }
    } else {
      setCompleted(sections.length - 1);
    }

    return () => {
      window.removeEventListener('click', clickEvent);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  useEffect(() => {
    SetActiveFeature({
      properties,
      isPropertyInSection,
      features,
      sections,
      activeIndex,
      setOwners,
      setData,
      isEdit,
      removeCreateForm,
      setIsShowCreateForm,
      appDataRef,
    });
    // eslint-disable-next-line
  }, [activeIndex, appDataRef.current]);

  const handleAddBusinessOwner = async () => {
    if (!isShowCreateForm) {
      setIsShowCreateForm(true);
      return;
    }
    const currentFeature = getCurrentFeature();
    const propertyGroup = currentFeature?.propertyGroup || false;

    const fields = getPropertyFields(currentFeature, data);
    const result = fields.map((field) => {
      // No validation check for hidden type
      if (field.type === 'hidden') {
        return true;
      }
      if (field.dependsOn) {
        if (field.condition != data[field.dependsOn]) {
          return true;
        } else {
          return validateInputField(data[field.name], field.name, field, setDataError);
        }
      } else {
        return validateInputField(data[field.name], field.name, field, setDataError);
      }
    });

    const isInValid = result.filter((r) => !r).length > 0;
    isInValid
      ? setSectionError('Please make sure all fields are filled in correctly.')
      : setSectionError('');

    if (isInValid) {
      return false;
    }

    if (owners.length < currentFeature.limits.maximum) {
      if (
        TenantConfig?.form.showCreditReportModal &&
        currentFeature &&
        currentFeature.itemType === 'ApplicantProperty' &&
        shouldDisplayCreditUnfreeze(appDataRef)
      ) {
        setMoveSectionOnCreditScoreModalClose(false); // Not moving to next section
        setShowCreditReportModal(true);
      } else {
        if (propertyGroup) {
          await updateAndValidateSection(false);
        } else {
          await updateAndValidateProperty(false, true);
        }
        handleUpdateOwners();
        showNewOwnerForm();
      }
    }
  };

  const handleCloseCreditModal = async () => {
    const currentFeature = getCurrentFeature();
    const propertyGroup = currentFeature?.propertyGroup || false;

    if (moveSectionOnCreditScoreModalClose) {
      let validateSuccess = await updateAndValidateProperty(false);
      if (currentFeature.itemType === 'ApplicantProperty') {
        handleUpdateOwners();
        if (!validateSuccess) {
          removeCreateForm();
        }
      }
    } else {
      if (propertyGroup) {
        await updateAndValidateSection(false);
      } else {
        await updateAndValidateProperty(false, true);
      }
      handleUpdateOwners();
      showNewOwnerForm();
    }
  };

  const showNewOwnerForm = async () => {
    /** initialize new form fields after adding owner */
    const initState = {};
    Object.keys(data).map((key) => {
      initState[key] = '';
    });
    initState.address = null;
    setData(initState);
    window.scrollTo(0, 0);
  };
  /** update owners state for rerendering */
  const handleUpdateOwners = () => {
    const applicantFeature = appDataRef.current?.applicationFeatures?.find(
      (item) => item.itemType === 'ApplicantProperty'
    );
    if (!applicantFeature) {
      return;
    }
    const ownersArray = applicantFeature.value;
    setOwners(
      ownersArray.map((ownerItem) => {
        return {
          ...ownerItem,
          address: ownerItem.address
            ? {
                ...ownerItem.address,
                label: ownerItem.address.deliveryLine1,
              }
            : null,
        };
      })
    );
  };

  /** sanitize function to clean the input data */
  const sanitizeData = (data) => {
    const activeFeature = features.find(
      (item) => item.section === sections[activeIndex.sectionIndex]
    );
    const currentFeature = getCurrentFeature();
    const fields = getPropertyFields(currentFeature, data);

    const dataFormatting = fields.reduce((a, b) => ({ ...a, [b.name]: b }), {});

    Object.keys(data).forEach((key) => {
      if (key == 'userId') {
        // for userId, don't need to sanitize : For owners
        return;
      }
      if (!dataFormatting[key] || dataFormatting[key].type == 'hidden') {
        delete data[key];
        return;
      }
      if (
        (dataFormatting[key].type == 'number' ||
          dataFormatting[key].type.includes('numberInput') ||
          dataFormatting[key].type == 'dollarInput') &&
        typeof data[key] === 'string'
      ) {
        data[key] =
          key == 'ownershipPercentage'
            ? parseInt(data[key])
            : parseFloat(data[key].split(',').join(''), 10);
      } else if (dataFormatting[key].type == 'date') {
        data[key] = moment(data[key]).format(
          dataFormatting[key].format ? dataFormatting[key].format : 'YYYY-MM-DD'
        );
      } else if (dataFormatting[key].type == 'shortDate') {
        data[key] = moment(data[key]).format(dataFormatting[key].format);
      }
    });

    // Check dependencies of fields and removes values if the condition is not met
    Object.keys(dataFormatting).forEach((key) => {
      const field = dataFormatting[key];
      if (field.type === 'hidden' && field.defaultValue) {
        data[key] = field.defaultValue;
      }
      if (field.dependsOn) {
        if (Array.isArray(data[field.dependsOn])) {
          if (!data[field.dependsOn].includes(field.condition)) delete data[key];
        } else {
          if (field.condition?.toString() !== data[field.dependsOn]?.toString()) delete data[key];
        }
      }
    });

    // For File Property, return files array data
    if (activeFeature['itemType'] === 'FileProperty') {
      const filesArray = Object.values(data).reduce(
        (previousValue, currentValue) => [...previousValue, ...currentValue],
        []
      );
      return filesArray.filter((item) => {
        return item.userAdded == false || item.id || (!item.error && !item.isCanceled);
      });
    }

    return data;
  };

  const handleMoveSection = () => {
    const currentFeature = getCurrentFeature();
    const propertyGroup = currentFeature?.propertyGroup || false;

    const sectionProperties = properties.filter((property) =>
      isPropertyInSection(features, sections[activeIndex.sectionIndex], property)
    );

    if (propertyGroup) {
      if (sections.length > activeIndex.sectionIndex + 1) {
        if (
          sections[activeIndex.sectionIndex + 1] === 'Apply' ||
          sections[activeIndex.sectionIndex + 1] === 'Review'
        ) {
          navigate('/review');
          return '';
        }
        setCompleted(activeIndex.sectionIndex + 1);
        setActiveIndex({
          sectionIndex: activeIndex.sectionIndex + 1,
          propertyIndex: 0,
        });
      } else {
        setCompleted(activeIndex.sectionIndex + 1);
        navigate('/review');
      }
    } else {
      if (sectionProperties.length > activeIndex.propertyIndex + 1) {
        setActiveIndex({
          sectionIndex: activeIndex.sectionIndex,
          propertyIndex: activeIndex.propertyIndex + 1,
        });
      } else if (sections.length > activeIndex.sectionIndex + 1) {
        if (
          sections[activeIndex.sectionIndex + 1] === 'Apply' ||
          sections[activeIndex.sectionIndex + 1] === 'Review'
        ) {
          navigate('/review');
          return '';
        }
        setCompleted(activeIndex.sectionIndex + 1);
        setActiveIndex({
          sectionIndex: activeIndex.sectionIndex + 1,
          propertyIndex: 0,
        });
      } else {
        setCompleted(activeIndex.sectionIndex + 1);
        navigate('/review');
      }
    }
    window.scrollTo(0, 0);
    setDataError({});
    setError('');
    setSectionError('');
    setIsShowCreateForm(1);
  };

  const handleSkip = async () => {
    handleMoveSection();
  };

  // Handle Continue Button click => Move to next property/section
  const handleContinue = async (goToDashboard) => {
    const currentFeature = getCurrentFeature();
    const propertyGroup = currentFeature?.propertyGroup || false;
    const fields = getPropertyFields(currentFeature, data);

    const result = fields.map((field) => {
      // No validation check for depending questions when the condition isn't met
      if (field.dependsOn) {
        if (Array.isArray(data[field.dependsOn])) {
          if (!data[field.dependsOn].includes(field.condition)) return true;
        } else {
          if (field.condition?.toString() !== data[field.dependsOn]?.toString()) return true;
        }
      }
      // No validation check for file type or hidden type
      if (field.type === 'file' || field.type === 'hidden') {
        return true;
      }
      return validateInputField(data[field.name], field.name, field, setDataError);
    });

    const isInValid = result.filter((r) => !r).length > 0;

    isInValid
      ? setSectionError('Please make sure all fields are filled in correctly.')
      : setSectionError('');

    if (isShowCreateForm && isInValid) {
      return false;
    }

    if (
      TenantConfig?.form.showCreditReportModal &&
      currentFeature &&
      currentFeature.itemType === 'ApplicantProperty' &&
      shouldDisplayCreditUnfreeze(appDataRef)
    ) {
      setMoveSectionOnCreditScoreModalClose(true);
      setShowCreditReportModal(true);
    } else {
      if (propertyGroup) {
        updateAndValidateSection(goToDashboard);
      } else {
        let validateSuccess = await updateAndValidateProperty(goToDashboard);
        if (currentFeature.itemType === 'ApplicantProperty') {
          handleUpdateOwners();
          if (!validateSuccess) {
            removeCreateForm();
          }
        }
      }
    }
  };

  const updateAndValidateSection = async (goToDashboard) => {
    const sectionProperties = properties.filter((property) =>
      isPropertyInSection(features, sections[activeIndex.sectionIndex], property)
    );
    const updateApiPayload = [];
    const validateProperties = [];
    sectionProperties.map((property) => {
      const propertyFeature = appData?.applicationFeatures?.find(
        (feature) => feature.name === property
      );
      const featureValue = getFeatureSanitizedData(propertyFeature);
      const featurePayload = {
        itemType: propertyFeature.itemType,
        dataType: propertyFeature.dataType,
        name: propertyFeature.name,
        value: featureValue,
      };
      updateApiPayload.push(featurePayload);
      validateProperties.push(property);
    });

    setLoading(true);
    setError('');

    try {
      const updatedData = await updateApplication(updateApiPayload);

      if (updatedData.data) {
        updateAppData(updatedData.data);
      }
      if (!goToDashboard) {
        const validationData = await validateProperty(validateProperties.join(','));

        setLoading(false);
        setDataError({});

        if (validationData.data && validationData.data.errors) {
          if (validationData.data.errors.length) {
            errorEvent('Error Validate', validationData.data.errors);
            validationData.data.errors.map((error) => {
              const attributes = error.property.split('.');
              setDataError((dataError) => ({
                ...dataError,
                [attributes[1]]: dataError[attributes[1]]
                  ? dataError[attributes[1]].concat('. ', error.message)
                  : error.message,
              }));
            });
            return false;
          }
        } else {
          setData({});
        }
      }
      setCompleted(activeIndex.sectionIndex);
    } catch (error) {
      NotificationManager.warning("The application couldn't be updated");
      errorEvent('Error API', error);
      const errors = error.response.data.errors;
      if (errors?.length) {
        setError(errors[0].message);
      }
      setLoading(false);
      return false;
    }

    if (isEdit) {
      navigate('/review');
    }

    if (goToDashboard) {
      navigate('/dashboard');
    } else {
      handleMoveSection();
    }
    setShowCreditReportModal(false);
    return true;
  };

  const getFeatureSanitizedData = (feature) => {
    if (feature && feature.itemType === 'ConsentProperty') {
      return [{ consented: true, userId: profile.id }];
    } else if (
      feature &&
      (feature.limits == null || feature.itemType === 'FileProperty') &&
      feature.itemType !== 'PlaidTokenProperty' &&
      feature.itemType !== 'IdVerifyProperty' &&
      feature.itemType !== 'SourceProperty' &&
      feature.itemType !== 'FinancialInstrumentProperty' &&
      feature.dataType !== 'loanPurposeDetails'
    ) {
      // For Company Property, check form fields validation
      const sanitizedData = sanitizeData(data);
      return sanitizedData;
    } else if (feature && feature.limits && feature.itemType !== 'FileProperty') {
      // For Applicant Property
      if (isShowCreateForm) {
        const sanitizedData = sanitizeData(data);
        return [
          ...owners,
          {
            ...sanitizedData,
          },
        ];
      }
    }
  };

  const updateAndValidateProperty = async (goToDashboard, notMoveToNext) => {
    const currentFeature = getCurrentFeature();
    if (!currentFeature) {
      return;
    }

    const updateApiPayload = [];
    const validateProperties = [];
    const featureValue = getFeatureSanitizedData(currentFeature);
    const featurePayload = {
      itemType: currentFeature.itemType,
      dataType: currentFeature.dataType,
      name: currentFeature.name,
      value: featureValue,
    };
    updateApiPayload.push(featurePayload);
    validateProperties.push(currentFeature.name);

    if (
      currentFeature &&
      currentFeature.itemType !== 'PlaidTokenProperty' &&
      currentFeature.itemType !== 'IdVerifyProperty' &&
      currentFeature.itemType !== 'SourceProperty' &&
      currentFeature.itemType !== 'FinancialInstrumentProperty' &&
      currentFeature.dataType !== 'loanPurposeDetails'
    ) {
      setLoading(true);
      setError('');

      try {
        const updatedData = await updateApplication(updateApiPayload);

        if (updatedData.data) {
          updateAppData(updatedData.data);
        }
        if (!goToDashboard) {
          const validationData = await validateProperty(validateProperties.join(','));

          setLoading(false);
          setDataError({});

          if (validationData.data && validationData.data.errors) {
            if (validationData.data.errors.length) {
              errorEvent('Error Validate', validationData.data.errors);

              if (currentFeature.itemType === 'ApplicantProperty') {
                const errorsArray = [{}, {}, {}, {}, {}];
                let hasError = false;
                validationData.data.errors.map((error) => {
                  /** Is submitted no applicant, should show applicant level error */
                  if (error.property === 'applicant') {
                    setError(error.message);
                    hasError = true;
                    return false;
                  }
                  const attributes = error.property.split('.');

                  const ownerIndex = attributes[1];
                  const ownerAttribute = attributes[2];
                  errorsArray[ownerIndex] = {
                    ...errorsArray[ownerIndex],
                    [ownerAttribute]: errorsArray[ownerIndex][ownerAttribute]
                      ? errorsArray[ownerIndex][ownerAttribute].concat('. ', error.message)
                      : error.message,
                  };
                });
                errorsArray.forEach((errors, index) => {
                  if (Object.keys(errors).length > 0) {
                    hasError = true;
                    if (index < owners.length) {
                    } else {
                      setDataError(errors);
                    }
                  }
                });
                return !hasError;
              } else {
                validationData.data.errors.map((error) => {
                  const attributes = error.property.split('.');
                  setDataError((dataError) => ({
                    ...dataError,
                    [attributes[1]]: dataError[attributes[1]]
                      ? dataError[attributes[1]].concat('. ', error.message)
                      : error.message,
                  }));
                });
              }
              return false;
            }
          } else {
            setData({});
          }
        }
        setCompleted(activeIndex.sectionIndex);
      } catch (error) {
        NotificationManager.warning("The application couldn't be updated");
        errorEvent('Error API', error);
        const errors = error.response.data.errors;
        if (errors?.length) {
          setError(errors[0].message);
        }
        setLoading(false);
        return false;
      }
    }

    if (isEdit && currentFeature.itemType !== 'PlaidTokenProperty') {
      /** When editing Plaid screen after review, we should go to next Financial screen even though isEdit is true */
      navigate('/review');
    }
    if (goToDashboard) {
      navigate('/dashboard');
    } else {
      if (!notMoveToNext) {
        handleMoveSection();
      }
    }
    setShowCreditReportModal(false);
    return true;
  };

  const refToHandleGoBackSectionWithProps = () => {
    const prepareProps = {
      setActiveIndex,
      activeIndex,
      setDataError,
      setError,
      setSectionError,
      sections,
    };
    return () => handleGoBackSection(prepareProps);
  };

  const refToHandleSkipToNextSectionProps = () => {
    const prepareProps = {
      setActiveIndex,
      activeIndex,
      setDataError,
      setError,
      setSectionError,
      setIsShowCreateForm,
    };
    return () => handleSkipToNextSection(prepareProps);
  };

  const refToHandleSaveFilePropertyWithProps = () => {
    const prepareProps = {
      appData,
      sanitizeData,
      setLoading,
      updateAppData,
      stateRef,
    };
    return () => handleSaveFileProperty(prepareProps);
  };

  const removeCreateForm = () => {
    setIsShowCreateForm(0);
  };

  const getCurrentFeature = () => {
    const sectionProperties = properties.filter((property) =>
      isPropertyInSection(features, sections[activeIndex.sectionIndex], property)
    );
    return features.find((item) => item.name === sectionProperties[activeIndex.propertyIndex]);
  };

  const toggleFieldModal = (fieldName) => {
    setIsModalOpen({
      ...isModalOpen,
      [fieldName]: !isModalOpen[fieldName],
    });
  };

  const initializeSectionError = () => {
    setSectionError('');
  };

  const preparingRenderFieldProps = {
    data,
    setData,
    dataError,
    setDataError,
    toggleFieldModal,
    isModalOpen,
    initializeSectionError,
    maskInputEl,
    feature: getCurrentFeature(),
  };

  const handleChangeSectionIndex = (index) => {
    if (index <= completed) {
      const sectionLength = TenantConfig?.stepper.showApplyStep
        ? sections.length - 1
        : sections.length;
      if (index < sectionLength) {
        setActiveIndex({
          sectionIndex: index,
          propertyIndex: 0,
        });
        setData({});
        setDataError({});
        setError('');
        setIsShowCreateForm(1);
      } else {
        navigate('/review');
      }
    }
  };

  const renderCoborrowerMessage = () => {
    const businessProp = appDataRef.current?.applicationFeatures?.find(
      (item) => item.dataType === 'business'
    );
    const loanPurposePro = appDataRef.current?.applicationFeatures?.find(
      (item) => item.dataType === 'loanPurposeDetails'
    );
    const applicantPro = appDataRef.current?.applicationFeatures?.find(
      (item) => item.itemType === 'ApplicantProperty'
    );
    const mainApplicantName =
      applicantPro?.value && applicantPro.value.length > 0
        ? applicantPro.value[0]['firstName'] + ' ' + applicantPro.value[0]['lastName']
        : null;

    if (
      TenantConfig?.getTenant() === 'sba' &&
      !isMainApplicant &&
      businessProp?.value &&
      businessProp.value['companyName'] &&
      mainApplicantName
    ) {
      return (
        <div className="section co-borrower">
          <p>
            <b>{mainApplicantName}</b> of <b>{businessProp?.value['companyName']}</b> has provided
            your information for a small business loan application in the amount of{' '}
            <b>{formatUSDCurrency(loanPurposePro?.value['estimatedLoanAmount'])}</b>.{' '}
          </p>
          <p>
            Please provide your information below to continue the loan application for the project
            including: <b>{loanPurposePro.value['loanPurpose'].toString().replaceAll(',', ', ')}</b>
            . Be aware, this prequalification round will not impact your credit.
          </p>
        </div>
      );
    } else {
      return null;
    }
  };

  const preparingPropsForRenderButton = {
    isMainApplicant,
    sectionError,
    handleAddBusinessOwner,
    updateAndValidateProperty,
    handleSkip,
    handleContinue,
    getCurrentFeature,
    appData,
  };

  const preparingPropsForRenderSection = {
    data,
    owners,
    profile,
    error,
    setError,
    preparingRenderFieldProps,
    isEdit,
    appData,
    features,
    sections,
    properties,
    closeTimes,
    activeIndex,
    plaidState,
    isModalOpen,
    updateAndValidateProperty,
    handleMoveSection,
    handleContinue,
    setPlaidState,
    setCloseTimes,
    currentSection: sections[activeIndex.sectionIndex],
    isShowCreateForm,
    toggleFieldModal,
    removeCreateForm,
    getCurrentFeature,
    getPropertyFields,
    isPropertyInSection,
    showCreditReportModal,
    handleSkipToNextSection: refToHandleSkipToNextSectionProps(),
    handleCloseCreditModal,
    initializeSectionError,
    handleSaveFileProperty: refToHandleSaveFilePropertyWithProps(),
    handleUpdateOwners,
    handleGoBackSection: refToHandleGoBackSectionWithProps(),
    editOwnerIndex,
    sanitizeData,
    isMainApplicant,
    appDataRef,
    setShowCreditReportModal,
  };

  if (
    sections[activeIndex.sectionIndex] === 'Apply' ||
    sections[activeIndex.sectionIndex] === 'Review'
  ) {
    navigate('/review');
    return '';
  }

  return (
    <div className="custom-form">
      <div>
        <Stepper
          sections={sections}
          activeIndex={activeIndex}
          completed={completed}
          handleChangeSectionIndex={handleChangeSectionIndex}
          isClickable={TenantConfig?.stepper.clickable}
        />
        <OfferDetail features={appData.applicationFeatures} />
      </div>
      {!isMainApplicant && renderCoborrowerMessage()}
      <div>{RenderCurrentSection(preparingPropsForRenderSection)}</div>
      {RenderButtonsFn(preparingPropsForRenderButton)}
      <ReactTooltip
        className="tooltip"
        place="bottom"
        textColor="#fcfcfaff"
        backgroundColor="#423176ff"
        effect="solid"
        html={true}
      />
    </div>
  );
}

export default PageForm;
